/**
 * AccountSecurity — the "Sign-in & security" account tab.
 *
 *   1. Password card: change an existing password (current + new), or, for a
 *      social-only account, SET a first password. Uses the shared PasswordField
 *      (two fields + strength gauge + generator).
 *   2. Connected accounts card: the user's sign-in methods — local password plus
 *      linked social providers (Google / Facebook / LinkedIn) with brand logos.
 *      Connect redirects through the hosted IdP; disconnect unlinks via the BFF
 *      (the server refuses to remove the last remaining sign-in method).
 *
 * All server calls go through the confidential BFF; nothing here needs tokens.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2, CheckCircle2, ShieldCheck, Link2, Unlink } from 'lucide-react';
import {
  nativeChangePassword, fetchLinkedAccounts, unlinkAccount, LinkedAccountsInfo,
} from '../../services/api';
import { PasswordField } from '../auth/PasswordField';
import { authErrorMessage } from '../../config/authErrors';

const API_URL = process.env.REACT_APP_API_URL || '';

// ---- Brand logos (inline; the app CSP blocks remote assets) ----------------

const GoogleLogo: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.5 6.2 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.5 6.2 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.4 26.7 36 24 36c-5.3 0-9.7-2.6-11.3-6.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2C39.9 35.9 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);

const FacebookLogo: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#1877F2" d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.47h-2.796v8.384C19.612 22.954 24 17.99 24 12z" />
  </svg>
);

const LinkedInLogo: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#0A66C2" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
  </svg>
);

const PROVIDERS: { id: string; name: string; Logo: React.FC }[] = [
  { id: 'google', name: 'Google', Logo: GoogleLogo },
  { id: 'facebook', name: 'Facebook', Logo: FacebookLogo },
  { id: 'linkedin', name: 'LinkedIn', Logo: LinkedInLogo },
];

/** Map a Keycloak IdP alias onto one of our canonical provider ids. */
const normalizeProvider = (alias: string): string => {
  const a = alias.toLowerCase();
  if (a.includes('google')) return 'google';
  if (a.includes('facebook')) return 'facebook';
  if (a.includes('linkedin')) return 'linkedin';
  return a;
};

export const AccountSecurity: React.FC = () => {
  const { t } = useTranslation('account');
  // ---- Linked accounts state ----
  const [info, setInfo] = useState<LinkedAccountsInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [busyProvider, setBusyProvider] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  // ---- Password form state ----
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwValid, setPwValid] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [pwSaved, setPwSaved] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  const loadInfo = useCallback(async () => {
    const data = await fetchLinkedAccounts();
    setInfo(data);
    setLoadingInfo(false);
  }, []);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const hasPassword = !!info?.hasPassword;
  const manageable = info?.manageable !== false;
  // Microsoft 365-managed staff identity. The server refuses every write on this
  // tab for these accounts (403 staff_managed) — this only explains why, in place
  // of the generic "not available" copy that covers a missing Admin API.
  const staffManaged = !!info?.staff;

  const handleSavePassword = async () => {
    setPwError(null);
    setPwSaved(false);
    if (!pwValid) { setPwError(t('auth:errors.choosePassword')); return; }
    if (hasPassword && !currentPassword) { setPwError(t('security.enterCurrent')); return; }
    setSavingPw(true);
    const res = await nativeChangePassword(hasPassword ? currentPassword : '', newPassword);
    setSavingPw(false);
    if (res.ok) {
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setPwValid(false);
      setPwSaved(true);
      setTimeout(() => setPwSaved(false), 4000);
      loadInfo(); // reflect "password now set" for a former social-only account
    } else {
      // invalid_credentials here means the *current* password was wrong, which
      // is a different sentence from a failed sign-in — hence the explicit case
      // rather than deferring the whole thing to the shared table.
      setPwError(res.error === 'invalid_credentials'
        ? t('security.currentIncorrect')
        : authErrorMessage(res, 'auth:errors.passwordUpdateFailed'));
    }
  };

  const connect = (id: string) => {
    const dest = '/account/security';
    window.location.assign(
      `${API_URL}/api/auth-login.php?kc_idp_hint=${encodeURIComponent(id)}&return_to=${encodeURIComponent(dest)}`,
    );
  };

  const disconnect = async (alias: string, id: string) => {
    setLinkError(null);
    setBusyProvider(id);
    const res = await unlinkAccount(alias);
    setBusyProvider(null);
    if (res.error) {
      setLinkError(authErrorMessage(res, 'account:security.disconnectFailed'));
      return;
    }
    setInfo(res);
  };

  return (
    <>
      {/* -------- Password -------- */}
      <div className="card shadow-sm mb-4 px-2 px-sm-0">
        <div className="card-body px-0 px-sm-4 py-4">
          <h5 className="fw-bold m-0 mb-1 d-flex align-items-center gap-2">
            <KeyRound size={18} /> {staffManaged ? t('auth:fields.password') : hasPassword ? t('security.changePassword') : t('security.setPassword')}
          </h5>
          <p className="text-muted small mb-3">
            {staffManaged
              ? t('security.staffIntro')
              : hasPassword
                ? t('security.changeIntro')
                : t('security.setIntro')}
          </p>

          {pwError && <div className="alert alert-warning py-2 small">{pwError}</div>}
          {pwSaved && (
            <div className="alert alert-success py-2 small d-flex align-items-center gap-2">
              <CheckCircle2 size={16} /> {t('security.passwordUpdated')}
            </div>
          )}

          {/* Wait for the snapshot rather than defaulting to "manageable" — a staff
              user should never see a password form flash before it's withdrawn. */}
          {loadingInfo ? (
            <div className="text-muted small d-flex align-items-center gap-2">
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> {t('loading')}
            </div>
          ) : staffManaged ? (
            <p className="text-muted small mb-0">{t('security.staffPasswordManaged')}</p>
          ) : !manageable ? (
            <p className="text-muted small mb-0">{t('security.passwordUnavailable')}</p>
          ) : (
            <div style={{ maxWidth: 420 }}>
              {hasPassword && (
                <div className="mb-3">
                  <label htmlFor="cp-current" className="form-label small fw-semibold mb-1">{t('security.currentPassword')}</label>
                  <input
                    id="cp-current" type="password" className="form-control" autoComplete="current-password"
                    value={currentPassword} disabled={savingPw}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </div>
              )}
              <PasswordField
                idPrefix="cp"
                label={hasPassword ? t('auth:fields.newPassword') : t('auth:fields.password')}
                value={newPassword}
                onChange={setNewPassword}
                confirmValue={confirmPassword}
                onConfirmChange={setConfirmPassword}
                disabled={savingPw}
                onValidChange={setPwValid}
              />
              <button
                className="btn btn-primary d-inline-flex align-items-center gap-2"
                disabled={savingPw || !pwValid || (hasPassword && !currentPassword)}
                onClick={handleSavePassword}
              >
                {savingPw && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                {hasPassword ? t('security.updatePassword') : t('security.setPasswordAction')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* -------- Connected accounts -------- */}
      <div className="card shadow-sm mb-4 px-2 px-sm-0">
        <div className="card-body px-0 px-sm-4 py-4">
          <h5 className="fw-bold m-0 mb-1 d-flex align-items-center gap-2">
            <ShieldCheck size={18} /> {t('security.connected')}
          </h5>
          <p className="text-muted small mb-3">
            {staffManaged ? t('security.connectedStaffIntro') : t('security.connectedIntro')}
          </p>

          {linkError && <div className="alert alert-warning py-2 small">{linkError}</div>}

          {loadingInfo ? (
            <div className="text-muted small d-flex align-items-center gap-2">
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> {t('loading')}
            </div>
          ) : staffManaged ? (
            <p className="text-muted small mb-0">{t('security.connectedStaffBody')}</p>
          ) : !manageable ? (
            <p className="text-muted small mb-0">{t('security.connectedUnavailable')}</p>
          ) : (
            <ul className="list-group list-group-flush">
              {/* Local password method */}
              <li className="list-group-item px-0 d-flex align-items-center justify-content-between gap-3">
                <span className="d-flex align-items-center gap-3">
                  <KeyRound size={20} className="text-secondary" />
                  <span>
                    <span className="fw-semibold d-block">{t('auth:fields.password')}</span>
                    <span className="text-muted small">{hasPassword ? t('security.enabled') : t('security.notSet')}</span>
                  </span>
                </span>
                {hasPassword && <CheckCircle2 size={18} className="text-success" />}
              </li>

              {/* Social providers */}
              {PROVIDERS.map(({ id, name, Logo }) => {
                const link = info?.linked.find((l) => normalizeProvider(l.provider) === id);
                const connected = !!link;
                const busy = busyProvider === id;
                return (
                  <li key={id} className="list-group-item px-0 d-flex align-items-center justify-content-between gap-3">
                    <span className="d-flex align-items-center gap-3">
                      <Logo />
                      <span>
                        <span className="fw-semibold d-block">{name}</span>
                        <span className="text-muted small">
                          {connected
                            ? (link?.userName ? t('security.connectedAs', { name: link.userName }) : t('security.connectedState'))
                            : t('security.notConnected')}
                        </span>
                      </span>
                    </span>
                    {connected ? (
                      <button
                        className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-2"
                        disabled={busy} onClick={() => disconnect(link!.provider, id)}
                      >
                        {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Unlink size={14} />}
                        {t('security.disconnect')}
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                        disabled={busy} onClick={() => connect(id)}
                      >
                        <Link2 size={14} /> {t('security.connect')}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountSecurity;
